import { useState } from 'react'
import {
  DollarSign,
  FileText,
  Hash,
  Pencil,
  Trash2,
  User,
  Users,
} from 'lucide-react'
import { capitalize } from 'lodash'
import { MemberForm } from './MemberForm'
import { DeleteMember } from './DeleteMember'
import { Badge } from '@/components/custom/badge'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/custom/modal'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/timeZoneUtils'
import ExportDialog from '@/features/receipts/components/ExportDialog'
import { usePermissions } from '@/utils/permissions'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CURRENT_YEAR } from '@/features/receipts/constants/receipt'
import { formatPhone } from '@/utils/inputFormat'

interface MemberDetailDialogProps {
  memberId: string
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}

export function MemberDetailDialog({
  memberId,
  trigger,
  open,
  onClose,
}: MemberDetailDialogProps) {
  const [activeTab, setActiveTab] = useState('details')
  const [openDialog, setOpenDialog] = useState({ edit: false, delete: false })

  const members = useMembersStore((s) => s.members)
  const churches = useChurchesStore((s) => s.churches)
  const donations = useDonationsStore((s) => s.donations)

  const permissions = usePermissions()

  const member = members.find((m) => m._id === memberId)
  const church = churches.find((c) => c._id === member?.churchId)
  const memberDonations = donations.filter((d) => d.donorId === memberId)

  if (!member) return null

  const hasEligibleDonations = memberDonations.some(
    (d) =>
      d.date &&
      !d.isRegistrationFee &&
      new Date(d.date).getFullYear() < CURRENT_YEAR,
  )

  const totalAmount = memberDonations.reduce((sum, d) => sum + d.amount, 0)

  const handleClose = () => {
    onClose?.()
    setActiveTab('details')
  }

  const footerActions = (
    <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
      {hasEligibleDonations && permissions.canSendReceipt() && (
        <ExportDialog
          id={null}
          type="annual"
          memberId={memberId}
          trigger={
            <Button variant="outline" size="sm">
              <FileText className="mr-2 h-4 w-4" /> Send Annual Report
            </Button>
          }
        />
      )}
      {permissions.canEditMember(member.churchId) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenDialog((d) => ({ ...d, edit: true }))}
        >
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </Button>
      )}
      {permissions.canDeleteMember() && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setOpenDialog((d) => ({ ...d, delete: true }))}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </Button>
      )}
    </div>
  )

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        trigger={trigger}
        title={`Member Details - ${member.name}`}
        direction="right"
        contentClassName="w-full md:min-w-5xl max-w-8xl h-fit"
        drawerContentClassName="!w-[98vw] !max-w-[98vw] !h-[100dvh]"
      >
        <div className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="details">
                <User className="h-4 w-4 mr-2" /> Information
              </TabsTrigger>
              <TabsTrigger value="donations">
                <FileText className="h-4 w-4 mr-2" /> Donations (
                {memberDonations.length})
              </TabsTrigger>
            </TabsList>

            {/* Information Tab */}
            <TabsContent value="details" className="mt-4">
              {/* Overview */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="flex items-center gap-2 justify-center">
                    <Hash className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Donations</p>
                      <p className="text-2xl font-semibold">
                        {memberDonations.length}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-2xl font-semibold">
                        {formatCurrency(totalAmount)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 justify-center">
                    <User className="h-5 w-5 text-red-700" />
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <Badge color={member.active ? 'green' : 'red'}>
                        {member.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium text-gray-600">Name:</span>
                      <p className="text-gray-900">{member.name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Email:</span>
                      <p className="text-gray-900">{member.email || '—'}</p>
                    </div>
                    {member.etransferEmail && (
                      <div>
                        <span className="font-medium text-gray-600">E-Transfer Email:</span>
                        <p className="text-gray-900">{member.etransferEmail}</p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-600">Phone:</span>
                      <p className="text-gray-900">
                        {member.phone ? formatPhone(member.phone) : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium text-gray-600">Church:</span>
                      <p className="text-gray-900">
                        {church ? church.name : 'Not assigned'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">
                        Address:
                      </span>
                      <p className="text-gray-900">
                        {member.address.street && (
                          <>
                            {member.address.street}
                            <br />
                          </>
                        )}
                        {member.address.city}, {member.address.province}{' '}
                        {member.address.postal}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Family Members */}
              {member.familyMembers && member.familyMembers.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-gray-500" />
                    <span className="font-medium text-gray-900">
                      Family Members
                    </span>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {member.familyMembers.map((fm, index) => {
                      const familyMember =
                        typeof fm === 'string'
                          ? members.find((m) => m._id === fm)
                          : members.find((m) => m._id === fm.memberId)
                      const relation =
                        typeof fm === 'string' ? 'Family Member' : fm.relation
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {familyMember?.name || 'Unknown Member'}
                          </span>
                          <Badge variant="outline">{relation}</Badge>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Donations Tab */}
            <TabsContent value="donations" className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Showing {memberDonations.length} donation
                {memberDonations.length !== 1 ? 's' : ''}
              </p>

              {memberDonations.length > 0 ? (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block">
                    <Table>
                      <THead>
                        <TR>
                          <TH>Receipt #</TH>
                          <TH>Date</TH>
                          <TH className="text-right">Amount</TH>
                          <TH>Method</TH>
                          <TH>Reference #</TH>
                          <TH>Status</TH>
                          <TH>Notes</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {memberDonations
                          .sort(
                            (a, b) =>
                              new Date(b.date).getTime() -
                              new Date(a.date).getTime(),
                          )
                          .map((donation) => (
                            <TR key={donation._id}>
                              <TD className="font-mono text-sm">
                                {donation.receiptNumber}
                              </TD>
                              <TD>
                                {formatDate(donation.date, 'MMM D, YYYY')}
                              </TD>
                              <TD className="text-right font-medium">
                                {formatCurrency(donation.amount)}
                              </TD>
                              <TD>{capitalize(donation.method)}</TD>
                              <TD className="font-mono text-sm">
                                {donation.donationRef || '—'}
                              </TD>
                              <TD>
                                <Badge
                                  color={
                                    donation.status === 'verified'
                                      ? 'green'
                                      : 'yellow'
                                  }
                                >
                                  {capitalize(donation.status)}
                                </Badge>
                              </TD>
                              <TD>{donation.notes || '—'}</TD>
                            </TR>
                          ))}
                      </TBody>
                    </Table>
                  </div>

                  {/* Mobile cards */}
                  <div className="space-y-3 md:hidden">
                    {memberDonations
                      .sort(
                        (a, b) =>
                          new Date(b.date).getTime() -
                          new Date(a.date).getTime(),
                      )
                      .map((donation) => (
                        <div
                          key={donation._id}
                          className="bg-gray-50 border rounded-lg p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-gray-700">
                              {donation.receiptNumber}
                            </span>
                            <Badge
                              color={
                                donation.status === 'verified'
                                  ? 'green'
                                  : 'yellow'
                              }
                            >
                              {capitalize(donation.status)}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              {formatDate(donation.date, 'MMM D, YYYY')}
                            </span>
                            <span className="font-semibold">
                              {formatCurrency(donation.amount)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span className="capitalize">
                              {donation.method}
                            </span>
                            {donation.donationRef && (
                              <span className="font-mono">
                                {donation.donationRef}
                              </span>
                            )}
                          </div>
                          {donation.notes && (
                            <p className="text-sm text-gray-700 truncate">
                              {donation.notes}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No donations found for this member
                </div>
              )}
            </TabsContent>
          </Tabs>

          {footerActions}
        </div>
      </Modal>

      {openDialog.edit && (
        <MemberForm
          id={member._id}
          type="edit"
          open={openDialog.edit}
          onClose={() => setOpenDialog((d) => ({ ...d, edit: false }))}
        />
      )}
      {openDialog.delete && (
        <DeleteMember
          id={member._id}
          open={openDialog.delete}
          onClose={() => {
            setOpenDialog((d) => ({ ...d, delete: false }))
            handleClose()
          }}
        />
      )}
    </>
  )
}
